import React, { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  Slide,
  IconButton,
  MenuItem,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getDifferenceBetweenStates } from 'src/utils/common';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { UPDATE_CLIENT_BILLING_DATA } from 'src/store/actions/clientActions';
import { isLoadingSelector } from 'src/store/selectors/statusSelector';
import { getBillingConceptList } from 'src/services/catalogService';
import selectBillingList from 'src/utils/selectBillingList';
import { BillingParameter } from 'src/utils/constants/parameters';
import makeInitialState from './initialState';
import validationSchema from './schema';
import useStyles from '../../../styles';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function UsoCfdiDialog({ open, handleClose, submit, cfdiUsage, taxSystemId }) {
  const { classes } = useStyles();
  const initialState = makeInitialState(cfdiUsage);
  const resetRef = useRef();
  const { t } = useTranslation();
  const [rawOptions, setRawOptions] = useState([]);
  const isSaving = useSelector(isLoadingSelector([UPDATE_CLIENT_BILLING_DATA.action]));

  const validOptions = taxSystemId
    ? rawOptions.filter(option =>
        option.description
          .split(',')
          .map(Number)
          .includes(taxSystemId)
      )
    : rawOptions;

  const cfdiOptions = selectBillingList({
    parameter: { name: BillingParameter.INVOICE_USE_CFDI },
    list: validOptions,
    t
  });

  const handleSubmitCfdiUsage = useCallback(
    (values, { resetForm }) => {
      const difference = getDifferenceBetweenStates(initialState, values);
      if (Object.entries(difference).length === 0) {
        handleClose();
        return;
      }
      submit({ ...difference });
      resetForm(initialState);
    },
    [submit, handleClose, initialState]
  );

  useEffect(() => {
    if (open) {
      getBillingConceptList()
        .then(data => setRawOptions(Array.isArray(data) ? data : []))
        .catch(() => {});
    }
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [resetRef, open]);

  if (!open) {
    return null;
  }

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      classes={{ paper: classes.root }}
      open={open}
      TransitionComponent={Transition}
      keepMounted
    >
      <DialogTitle className={classes.header} id="form-cfdi-usage-dialog-title">
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={handleClose}
          className={classes.closeIcon}
          disabled={isSaving}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.header} color="primary" variant="h2">
          {t('CLIENTS_VIEW.BILLING_DETAIL.EDIT_CFDI_USAGE')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Formik
          enableReinitialize
          initialValues={initialState}
          validationSchema={validationSchema}
          onSubmit={handleSubmitCfdiUsage}
        >
          {({ errors, handleChange, handleSubmit, touched, values, resetForm }) => {
            resetRef.current = () => resetForm();
            return (
              <form onSubmit={handleSubmit}>
                <TextField
                  select
                  fullWidth
                  label={t('CLIENTS_VIEW.BILLING_DETAIL.CFDI_USAGE')}
                  margin="normal"
                  name="cfdiUsage"
                  onChange={handleChange}
                  value={values.cfdiUsage}
                  variant="outlined"
                  error={Boolean(touched.cfdiUsage && errors.cfdiUsage)}
                  helperText={touched.cfdiUsage && t(errors.cfdiUsage)}
                  disabled={isSaving}
                >
                  {cfdiOptions.map(option => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>

                <Grid container direction="column" alignItems="stretch">
                  <Grid item className={classes.buttons}>
                    <Button
                      className={clsx(classes.label, classes.buttons)}
                      type="submit"
                      color="primary"
                      variant="outlined"
                      disabled={isSaving}
                      endIcon={
                        isSaving ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : null
                      }
                    >
                      {t('COMMON.SAVE')}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

UsoCfdiDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  cfdiUsage: PropTypes.string,
  taxSystemId: PropTypes.number
};

export default UsoCfdiDialog;
